/**
 * Auth React Query hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '../services/authService';
import type { UpdateProfilePayload } from '../../../entities/user';

export const authKeys = {
    currentUser: ['auth', 'me'] as const,
    user: (id: string) => ['auth', 'user', id] as const,
};

/** Fetch the currently authenticated user */
export function useCurrentUser(enabled = true) {
    return useQuery({
        queryKey: authKeys.currentUser,
        queryFn: () => authService.getCurrentUser(),
        enabled,
    });
}

/** Mutation to update the current user's profile */
export function useUpdateProfile() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: (data: UpdateProfilePayload) => authService.updateProfile(data),
        onSuccess: (updatedUser) => {
            qc.setQueryData(authKeys.currentUser, updatedUser);
        },
    });
}

/** Mutation to simulate upgrading to Pro tier */
export function useUpgradeToPro() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: () => authService.upgradeToPro(),
        onSuccess: (updatedUser) => {
            qc.setQueryData(authKeys.currentUser, updatedUser);
            // Invalidate other queries that might depend on tier
            qc.invalidateQueries({ queryKey: authKeys.currentUser });
        },
    });
}

/** Fetch a user by ID (admin) */
export function useUser(userId: string, enabled = true) {
    return useQuery({
        queryKey: authKeys.user(userId),
        queryFn: () => authService.getUserById(userId),
        enabled,
    });
}
